import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase, syncRealtimeToken } from '../services/supabase';
import { Session, User, AuthError, Factor, AuthMFAEnrollResponse } from '@supabase/supabase-js';
import { auditLogService } from '../services/auditLogService';
import { performPostLoginHandshake, type HandshakeResult } from '../services/offlineStorage';
import { queryClient } from '../services/queryClient';

// Types derived from our database schema
export interface Tenant {
  id: string;
  name: string;
  slug: string;
  role: 'super_admin' | 'tenant_admin' | 'regional_manager' | 'store_manager' | 'auditor';
  maturity_level: number;
  is_onboarded: boolean;
  currency?: string;
  locale?: string;
  permissions?: {
    stores?: string[];
    can_manage_users?: boolean;
    can_manage_inventory?: boolean;
    can_manage_alerts?: boolean;
    can_view_reports?: boolean;
    can_manage_cases?: boolean;
  };
}

/**
 * Post-Login Handshake Protocol - Routing Status
 *
 * Determines where the user should be routed after successful authentication:
 * - 'founder': User has no tenant_id (brand new user, needs to create organization)
 * - 'incomplete': User has tenant_id but is_onboarded is FALSE (needs to complete setup)
 * - 'operator': User has tenant_id AND is_onboarded is TRUE (go directly to dashboard)
 * - 'pending': Initial state, authentication in progress
 * - 'error': Authentication or profile fetch failed
 */
export type OnboardingStatus = 'pending' | 'founder' | 'incomplete' | 'operator' | 'error';

export type UserRole = 'super_admin' | 'tenant_admin' | 'regional_manager' | 'store_manager' | 'auditor';

interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  mfaRequired: boolean;
  mfaFactors: Factor[];
  handshakeComplete: boolean;
  handshakeResult: HandshakeResult | null;
  onboardingStatus: OnboardingStatus;
}

interface AuthContextType extends AuthState {
  // Standard auth
  signIn: (email: string, password?: string) => Promise<{ success: boolean; error?: string; mfaRequired?: boolean }>;
  signUp: (email: string, password: string, fullName: string, tenantId: string, role: UserRole) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ success: boolean; error?: string }>;
  updatePassword: (newPassword: string) => Promise<{ success: boolean; error?: string }>;

  // MFA (2FA) functions
  enrollMFA: () => Promise<{ success: boolean; qrCode?: string; secret?: string; factorId?: string; error?: string }>;
  verifyMFA: (factorId: string, code: string) => Promise<{ success: boolean; error?: string }>;
  unenrollMFA: (factorId: string) => Promise<{ success: boolean; error?: string }>;
  challengeMFA: (factorId: string) => Promise<{ success: boolean; challengeId?: string; error?: string }>;
  verifyMFAChallenge: (factorId: string, challengeId: string, code: string) => Promise<{ success: boolean; error?: string }>;

  // Impersonation (for support)
  impersonateUser: (userId: string) => Promise<{ success: boolean; error?: string }>;
  stopImpersonation: () => Promise<void>;
  isImpersonating: boolean;
  originalUser: User | null;

  // Refresh
  refreshSession: () => Promise<void>;

  // Post-login handshake
  performHandshake: () => Promise<HandshakeResult>;

  // Onboarding
  markOnboardingComplete: () => Promise<boolean>;

  // Error recovery
  retryProfile: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Roles that require 2FA
const MFA_REQUIRED_ROLES: UserRole[] = ['regional_manager', 'super_admin', 'tenant_admin'];
const LOGIN_BLOCK_CHECK_ENABLED = import.meta.env.VITE_ENABLE_LOGIN_BLOCK_CHECK === 'true';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaFactors, setMfaFactors] = useState<Factor[]>([]);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  const [handshakeComplete, setHandshakeComplete] = useState(false);
  const [handshakeResult, setHandshakeResult] = useState<HandshakeResult | null>(null);
  const [onboardingStatus, setOnboardingStatus] = useState<OnboardingStatus>('pending');

  /**
   * Fetch tenant profile from users table and determine onboarding status
   *
   * POST-LOGIN HANDSHAKE PROTOCOL:
   * - Scenario A (Founder): tenant_id is NULL → status = 'founder' → route to onboarding
   * - Scenario B (Incomplete): tenant_id exists but is_onboarded is FALSE → status = 'incomplete' → route to onboarding
   * - Scenario C (Operator): tenant_id exists AND is_onboarded is TRUE → status = 'operator' → route to dashboard
   */
  const fetchTenantProfile = useCallback(async (authUser: User) => {
    try {
      // P0.2 FIX: Retry with exponential backoff (3 attempts: 1s, 2s, 4s)
      // Handles cross-tab token refresh races, slow PostgREST cold starts,
      // and transient network issues that caused dead-end 'error' states.
      const MAX_RETRIES = 3;
      const BACKOFF_BASE_MS = 1000;
      let userProfile: any = null;
      let userError: any = null;

      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        const result = await supabase
          .from('users')
          .select('*')
          .eq('email', authUser.email)
          .single();

        userProfile = result.data;
        userError = result.error;

        if (!userError && userProfile) break; // Success

        if (attempt < MAX_RETRIES) {
          const delayMs = BACKOFF_BASE_MS * Math.pow(2, attempt); // 1s, 2s, 4s
          if (import.meta.env.DEV) console.warn(`[Handshake] Profile fetch attempt ${attempt + 1} failed, retrying in ${delayMs}ms:`, userError?.message);
          await new Promise(r => setTimeout(r, delayMs));
        }
      }

      if (userError) {
        if (import.meta.env.DEV) console.warn('[Handshake] User profile not found after retries, checking metadata:', userError);
      }

      // SCENARIO A: User exists but has NO tenant_id (The Founder)
      if (userProfile && !userProfile.tenant_id) {
        if (import.meta.env.DEV) console.log('[Handshake] Scenario A - FOUNDER: User has no tenant_id');
        setTenant(null);
        setOnboardingStatus('founder');
        setLoading(false);
        return;
      }

      // User has tenant assignment
      if (userProfile?.tenant_id) {
        const { data: tenantProfile, error: tenantProfileError } = await supabase
          .from('tenants')
          .select('*')
          .eq('id', userProfile.tenant_id)
          .single();

        if (tenantProfileError || !tenantProfile) {
          if (import.meta.env.DEV) console.warn('[Handshake] Tenant profile not found for user tenant_id:', tenantProfileError);
          setTenant(null);
          setOnboardingStatus('founder');
          setLoading(false);
          return;
        }

        // Check is_onboarded flag, with fallback for legacy databases without the column
        // If column doesn't exist, use tenant maturity_level >= 2 as proxy for "onboarded"
        const hasOnboardedFlag = userProfile.is_onboarded !== undefined || tenantProfile.is_onboarded !== undefined;
        const isUserOnboarded = hasOnboardedFlag
          ? (userProfile.is_onboarded ?? tenantProfile.is_onboarded ?? false)
          : (tenantProfile.maturity_level >= 2); // Fallback: maturity >= 2 means configured

        const tenantData = {
          id: userProfile.tenant_id,
          name: tenantProfile.name,
          slug: tenantProfile.slug,
          role: userProfile.role as UserRole,
          maturity_level: tenantProfile.maturity_level || 1,
          is_onboarded: isUserOnboarded,
          currency: tenantProfile.currency_code || 'USD',
          locale: tenantProfile.locale || 'es-CL',
          permissions: userProfile.permissions || {},
        };
        setTenant(tenantData);

        // SCENARIO B: User has tenant but is NOT onboarded (The Incomplete)
        if (!isUserOnboarded) {
          if (import.meta.env.DEV) console.log('[Handshake] Scenario B - INCOMPLETE: User has tenant but is_onboarded=false');
          setOnboardingStatus('incomplete');
        }
        // SCENARIO C: User has tenant AND is onboarded (The Operator)
        else {
          if (import.meta.env.DEV) console.log('[Handshake] Scenario C - OPERATOR: User is fully onboarded, routing to dashboard');
          setOnboardingStatus('operator');
        }

        // Log RLS verification for admin debugging
        if (import.meta.env.DEV) console.log('[Auth] RLS Policy Check - tenant_id:', tenantData.id);

        // Check if MFA is required for this role
        // Pass role directly to avoid stale closure (tenant state hasn't propagated yet)
        const userRole = userProfile.role as UserRole;
        if (MFA_REQUIRED_ROLES.includes(userRole)) {
          await checkMFAStatus(userRole);
        }
      } else {
        // Fallback to user metadata or create demo tenant
        const metadata = authUser.user_metadata;
        const appMetadata = authUser.app_metadata;

        if (appMetadata?.tenant_id || metadata?.tenant_id) {
          // User has tenant_id in metadata, fetch tenant
          const tenantId = appMetadata?.tenant_id || metadata?.tenant_id;
          const { data: tenantData } = await supabase
            .from('tenants')
            .select('*')
            .eq('id', tenantId)
            .single();

          if (tenantData) {
            const isOnboarded = tenantData.is_onboarded ?? false;
            setTenant({
              id: tenantData.id,
              name: tenantData.name,
              slug: tenantData.slug,
              role: (appMetadata?.role || metadata?.role || 'store_manager') as UserRole,
              maturity_level: tenantData.maturity_level || 1,
              is_onboarded: isOnboarded,
              currency: tenantData.currency_code || 'USD',
              locale: tenantData.locale || 'es-CL',
            });
            setOnboardingStatus(isOnboarded ? 'operator' : 'incomplete');
          } else {
            // Tenant not found in DB but ID was in metadata - treat as founder
            if (import.meta.env.DEV) console.warn('[Handshake] Tenant ID in metadata but tenant not found');
            setOnboardingStatus('founder');
            setTenant(null);
          }
        } else {
          // No tenant found anywhere - user needs to create organization (Founder)
          if (import.meta.env.DEV) console.log('[Handshake] Scenario A - FOUNDER: No tenant assignment found anywhere');
          setOnboardingStatus('founder');
          setTenant(null);
        }
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Handshake] Error fetching tenant profile:', err);
      setError('Failed to load user profile. Please try again.');
      setOnboardingStatus('error');
      setTenant(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check MFA enrollment status
  const checkMFAStatus = useCallback(async (role?: UserRole) => {
    try {
      const { data, error } = await supabase.auth.mfa.listFactors();
      if (error) throw error;

      const totp = data?.totp || [];
      setMfaFactors(totp);

      // Use the passed role (avoids stale closure on tenant state)
      const effectiveRole = role ?? tenant?.role;
      if (effectiveRole && MFA_REQUIRED_ROLES.includes(effectiveRole) && totp.length === 0) {
        setMfaRequired(true);
      } else {
        setMfaRequired(false);
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('Error checking MFA status:', err);
    }
  }, [tenant]);

  // Initialize auth state
  useEffect(() => {
    // Get initial session with timeout to handle stale/invalid tokens.
    // When localStorage has an invalid token, getSession() can hang indefinitely
    // as Supabase tries to refresh a revoked token. This timeout ensures the app
    // doesn't get stuck on the loading skeleton.
    //
    // RACE CONDITION FIX (v3.0.5):
    // Both getSession().then() and onAuthStateChange(INITIAL_SESSION) fire for the
    // initial session. Previously, INITIAL_SESSION set user but didn't call
    // fetchTenantProfile (only SIGNED_IN did), creating a window where user != null
    // but tenant == null. If getSession() then timed out, tenant was never fetched.
    // Fix: Use processSession() guard to ensure exactly one path processes the
    // initial session and always calls fetchTenantProfile.
    const SESSION_TIMEOUT_MS = 15000;
    let timedOut = false;
    let initialSessionProcessed = false;
    let profileFetchStarted = false;

    // Atomic session processor — ensures initial session is handled exactly once,
    // regardless of whether INITIAL_SESSION or getSession().then() fires first.
    const processSession = (session: Session | null) => {
      if (initialSessionProcessed) return;
      initialSessionProcessed = true;
      clearTimeout(timeoutId); // Cancel timeout — session resolved normally

      setSession(session);
      setUser(session?.user ?? null);
      // Sync Realtime WebSocket with initial access token
      if (session?.access_token) {
        syncRealtimeToken(session.access_token);
      }
      if (session?.user) {
        profileFetchStarted = true;
        fetchTenantProfile(session.user);
      } else {
        setLoading(false);
      }
    };

    const sessionPromise = supabase.auth.getSession();
    let timeoutId: ReturnType<typeof setTimeout>;
    const timeoutPromise = new Promise<{ data: { session: null }; error: Error }>((resolve) => {
      timeoutId = setTimeout(() => {
        timedOut = true;
        if (import.meta.env.DEV) console.warn('[Auth] Session restoration timed out after', SESSION_TIMEOUT_MS, 'ms');
        resolve({ data: { session: null }, error: new Error('Session timeout') });
      }, SESSION_TIMEOUT_MS);
    });

    Promise.race([sessionPromise, timeoutPromise]).then(({ data: { session }, error }) => {
      if (error || timedOut) {
        // Session restoration timed out or errored - do NOT clear localStorage tokens.
        // Clearing tokens on timeout causes session loss on page reload when
        // the network is slow or token refresh takes longer than expected.
        // Instead, just proceed without a session and let the user re-login if needed.
        if (!initialSessionProcessed) {
          initialSessionProcessed = true;
          if (import.meta.env.DEV) console.warn('[Auth] Proceeding without session due to', error ? 'error' : 'timeout');
          setLoading(false);
        }
        return;
      }

      processSession(session);
    });

    // Listen for auth changes.
    // CRITICAL: Do NOT use `await` on Supabase operations inside this callback.
    // auth-js holds an internal lock during _initialize(). Awaiting async Supabase
    // calls here deadlocks _initialize(), preventing getSession() and INITIAL_SESSION
    // from ever resolving (see https://github.com/supabase/auth-js/issues/762).
    // Instead, use fire-and-forget: fetchTenantProfile(user) without await.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // INITIAL_SESSION fires after _initialize() completes with the resolved session.
      // Process it through the same guard as getSession() — whichever fires first wins.
      // LATE RECOVERY: If timeout already fired but INITIAL_SESSION has a valid session,
      // still process it to recover from the timeout state.
      if (event === 'INITIAL_SESSION') {
        if (!initialSessionProcessed) {
          processSession(session);
        } else if (!profileFetchStarted && session?.user) {
          // Late recovery: getSession() resolved first with null/error/timeout,
          // but INITIAL_SESSION now has a valid session (e.g., from cross-tab sync).
          if (import.meta.env.DEV) console.log('[Auth] Late session recovery - processing INITIAL_SESSION after getSession failed');
          profileFetchStarted = true;
          setLoading(true);
          setSession(session);
          setUser(session.user);
          fetchTenantProfile(session.user);
        }
        return;
      }

      // Sync Realtime WebSocket token on every auth state change that has a session.
      if (session?.access_token) {
        syncRealtimeToken(session.access_token);
      }

      // TOKEN_REFRESHED can fire BEFORE INITIAL_SESSION when the stored token is expired.
      // auth-js internally refreshes during _initialize() and emits TOKEN_REFRESHED mid-flight.
      // If we haven't processed the initial session yet, treat this as the initial session
      // to prevent the 15s timeout from firing and causing a flash of the error recovery screen.
      if (event === 'TOKEN_REFRESHED' && !initialSessionProcessed && session?.user) {
        if (import.meta.env.DEV) console.log('[Auth] TOKEN_REFRESHED before initial session — treating as initial session');
        processSession(session);
        return;
      }

      // After timeout, recover from late TOKEN_REFRESHED that arrives with a valid session.
      if (timedOut && event === 'TOKEN_REFRESHED') {
        if (session?.user) {
          if (import.meta.env.DEV) console.log('[Auth] Late TOKEN_REFRESHED recovery after timeout');
          setSession(session);
          setUser(session.user);
          if (!profileFetchStarted) {
            profileFetchStarted = true;
            setLoading(true);
            fetchTenantProfile(session.user);
          }
        }
        return;
      }

      setSession(session);
      setUser(session?.user ?? null);

      if (event === 'SIGNED_IN' && session?.user) {
        // Set loading=true to prevent error recovery screen from flashing
        // while fetchTenantProfile is in progress
        profileFetchStarted = true;
        setLoading(true);
        fetchTenantProfile(session.user);
      } else if (event === 'SIGNED_OUT') {
        // CRITICAL: Clear query cache when session ends (logout, expiry, or revoked from another tab)
        queryClient.clear();
        if (import.meta.env.DEV) console.log('[Auth] Query cache cleared on SIGNED_OUT event');

        profileFetchStarted = false;
        setTenant(null);
        setMfaRequired(false);
        setMfaFactors([]);
        setIsImpersonating(false);
        setOriginalUser(null);
        setLoading(false);
        setOnboardingStatus('pending');
      } else if (event === 'MFA_CHALLENGE_VERIFIED') {
        // MFA challenge completed successfully
        if (session?.user) {
          profileFetchStarted = true;
          fetchTenantProfile(session.user);
        }
      } else if (session?.user && !profileFetchStarted) {
        // Safety net: TOKEN_REFRESHED or other event brought a valid session
        // but we never fetched the tenant profile.
        if (import.meta.env.DEV) console.log('[Auth] Late profile recovery -', event, 'has user but profile never fetched');
        profileFetchStarted = true;
        setLoading(true);
        fetchTenantProfile(session.user);
      }
    });

    return () => {
      clearTimeout(timeoutId);
      subscription.unsubscribe();
    };
  }, [fetchTenantProfile]);

  // Self-healing: detect inconsistent state where user is set but profile was never loaded.
  // This catches ALL race conditions (cross-tab TOKEN_REFRESHED, late INITIAL_SESSION, etc.)
  // regardless of the specific event ordering. If user exists, loading is done, but
  // onboardingStatus is still 'pending' (meaning fetchTenantProfile never completed),
  // automatically retry. This prevents the "Error al cargar perfil" screen.
  useEffect(() => {
    if (user && !loading && onboardingStatus === 'pending') {
      if (import.meta.env.DEV) console.warn('[Auth] Self-healing: refreshing session then retrying profile fetch');
      let cancelled = false;
      const recover = async () => {
        setLoading(true);
        try {
          const { data, error } = await supabase.auth.refreshSession();
          if (cancelled) return;
          if (error || !data.session) {
            if (import.meta.env.DEV) console.error('[Auth] Self-healing: session refresh failed:', error?.message);
            setOnboardingStatus('error');
            setLoading(false);
            return;
          }
          setSession(data.session);
          setUser(data.session.user);
          await fetchTenantProfile(data.session.user);
        } catch (err) {
          if (cancelled) return;
          if (import.meta.env.DEV) console.error('[Auth] Self-healing recovery failed:', err);
          setOnboardingStatus('error');
          setLoading(false);
        }
      };
      recover();
      return () => { cancelled = true; };
    }
  }, [user, loading, onboardingStatus, fetchTenantProfile]);

  // Tab-foreground recovery: safety net for broken auth state after backgrounding.
  // Supabase auth-js has its own visibilitychange handler that refreshes tokens,
  // but our React state can still be stuck (e.g., timeout fired, profile fetch failed).
  // This waits for Supabase's handler to complete, then checks and recovers.
  useEffect(() => {
    let recoveryInProgress = false;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      if (recoveryInProgress) return;

      // Delay: let Supabase's internal handler refresh the token first (~2s)
      setTimeout(async () => {
        const needsRecovery =
          (user && !tenant && !loading) ||
          (onboardingStatus === 'error' && user);

        if (!needsRecovery) return;

        if (import.meta.env.DEV) console.log('[Auth] Tab-foreground recovery: broken state detected, attempting recovery');
        recoveryInProgress = true;

        try {
          const { data, error } = await supabase.auth.refreshSession();
          if (error || !data.session) {
            if (import.meta.env.DEV) console.warn('[Auth] Tab-foreground recovery: session expired');
            recoveryInProgress = false;
            return; // Don't force signout — let user click "Cerrar Sesión"
          }

          setSession(data.session);
          setUser(data.session.user);
          setError(null);
          setOnboardingStatus('pending');
          setLoading(true);
          await fetchTenantProfile(data.session.user);
        } catch (err) {
          if (import.meta.env.DEV) console.error('[Auth] Tab-foreground recovery failed:', err);
        } finally {
          recoveryInProgress = false;
        }
      }, 2000);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user, tenant, loading, onboardingStatus, fetchTenantProfile]);

  // Sign in with email and password (or demo mode if no password)
  // NOTE: We don't set global loading=true here because AuthGateway has its own loading state
  // Setting global loading would cause App.tsx to show skeleton and unmount AuthGateway, losing error state
  const signIn = async (email: string, password?: string): Promise<{ success: boolean; error?: string; mfaRequired?: boolean }> => {
    setError(null);

    // P1.7: Network connectivity check — show clear message instead of vague timeout
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      const offlineMsg = 'Sin conexión a internet. Verifique su red.';
      setError(offlineMsg);
      return { success: false, error: offlineMsg };
    }

    // =====================================================================
    // SECURITY: Demo Mode Authentication
    // =====================================================================
    // Demo mode is ONLY allowed when ALL of the following conditions are met:
    // 1. VITE_DEMO_MODE_ENABLED environment variable is explicitly set to 'true'
    // 2. Application is running in development mode (import.meta.env.DEV === true)
    // 3. User provides no password (empty password triggers demo flow)
    //
    // CRITICAL SECURITY NOTES:
    // - Demo mode uses REAL Supabase authentication with a dedicated demo account
    // - Demo mode DOES NOT bypass RLS policies - it authenticates as a real user
    // - The demo user has READ-ONLY access to a sandboxed demo tenant's data
    // - Production builds (import.meta.env.PROD) will NEVER allow demo mode
    // =====================================================================
    const isDemoModeEnabled = import.meta.env.VITE_DEMO_MODE_ENABLED === 'true';
    const isDevEnvironment = import.meta.env.DEV === true;
    // Demo mode only triggers when the caller supplied no password (per comment above).
    // Real password submissions always go through the standard password/MFA path below.
    const isDemoMode = isDemoModeEnabled && isDevEnvironment && !password;

    // SECURITY: Block demo mode in production, even if env var is misconfigured
    if (isDemoModeEnabled && !isDevEnvironment) {
      if (import.meta.env.DEV) console.error('[SECURITY VIOLATION] Demo mode env var is set in production build - BLOCKED');
      auditLogService.logLoginFailed(email, 'demo_mode_blocked_in_production');
      setError('Demo mode is disabled in production');
      return { success: false, error: 'Demo mode is not available' };
    }

    if (isDemoMode) {
      if (import.meta.env.DEV) {
        console.warn('[SECURITY] Demo mode authentication - Development environment only');
        console.log('[Handshake] Demo mode authentication for:', email);
      }

      // Authenticate first with the demo password, then let onAuthStateChange handle profile
      try {
        const demoPassword = import.meta.env.VITE_DEMO_PASSWORD || 'Demo2024!Secure';

        const { error: authError } = await supabase.auth.signInWithPassword({
          email,
          password: demoPassword,
        });

        if (authError) {
          if (import.meta.env.DEV) {
            console.error('[Handshake] Demo mode: Supabase auth failed:', authError.message);
            console.warn('[CONFIG] Demo users must be created in Supabase Auth with the demo password');
          }
          const msg = authError.message === 'Invalid login credentials'
            ? 'Correo o contraseña incorrectos'
            : 'Error de autenticación';
          setError(msg);
          return { success: false, error: msg };
        }

        // Real Supabase auth succeeded — SIGNED_IN event will fire,
        // and the onAuthStateChange listener will call fetchTenantProfile.
        if (import.meta.env.DEV) console.log('[Handshake] Demo mode: Real Supabase session established — listener will handle profile');
        return { success: true };
      } catch (err) {
        if (import.meta.env.DEV) console.error('[Handshake] Demo mode: error:', err);
        setError('Error de conexión. Intente de nuevo.');
        return { success: false, error: 'Error de conexión' };
      }
    }

    // Real authentication with password
    const SIGN_IN_TIMEOUT_MS = 12000;
    try {
      // P0.3: Optional brute-force protection. Some tenant databases do not
      // expose this RPC yet, so keep it disabled unless the environment opts in.
      if (LOGIN_BLOCK_CHECK_ENABLED) {
        try {
          const rpcResult = await Promise.race([
            supabase.rpc('is_login_blocked', { p_email: email }),
            new Promise<{ data: null }>((resolve) =>
              setTimeout(() => resolve({ data: null }), 5000)
            ),
          ]);
          if (rpcResult?.data === true) {
            const blockedMsg = 'Demasiados intentos fallidos. Intente de nuevo en 15 minutos.';
            setError(blockedMsg);
            return { success: false, error: blockedMsg };
          }
        } catch {
          // Non-critical: proceed with auth if rate limit check fails
        }
      }

      // P0.1 FIX: Use a resolving timeout sentinel instead of a rejecting timeout.
      // If signInWithPassword succeeds after the timeout, the onAuthStateChange('SIGNED_IN')
      // listener will still handle it cleanly — no split-brain between AuthGateway and listener.
      const TIMEOUT_SENTINEL = Symbol('timeout');
      const result = await Promise.race([
        supabase.auth.signInWithPassword({ email, password }),
        new Promise<typeof TIMEOUT_SENTINEL>((resolve) =>
          setTimeout(() => resolve(TIMEOUT_SENTINEL), SIGN_IN_TIMEOUT_MS)
        ),
      ]);

      if (result === TIMEOUT_SENTINEL) {
        // Timeout fired — return error to caller but DON'T set global error state.
        // The in-flight signInWithPassword may still succeed; if it does,
        // the auth listener will process SIGNED_IN and log the user in.
        if (import.meta.env.DEV) console.warn('[Auth] signIn timed out after', SIGN_IN_TIMEOUT_MS, 'ms — auth request still in-flight');
        return { success: false, error: 'Tiempo de espera agotado. Verifique su conexión.' };
      }

      const { data, error: signInError } = result;

      if (signInError) {
        // Map Supabase errors to user-friendly Spanish messages
        let userMessage = 'Credenciales inválidas';
        let failureReason = signInError.message;
        if (signInError.message.includes('Invalid login credentials')) {
          userMessage = 'Correo o contraseña incorrectos';
          failureReason = 'invalid_credentials';
        } else if (signInError.message.includes('Email not confirmed')) {
          userMessage = 'Correo no confirmado. Revise su bandeja de entrada.';
          failureReason = 'email_not_confirmed';
        } else if (signInError.message.includes('User not found')) {
          userMessage = 'Usuario no registrado';
          failureReason = 'user_not_found';
        }

        // P0.3: Log failed login attempt to database
        auditLogService.logLoginFailed(email, failureReason);

        setError(userMessage);
        return { success: false, error: userMessage };
      }

      // Check if MFA is required
      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      const hasEnrolledFactors = (factorsData?.totp?.length || 0) > 0;

      if (hasEnrolledFactors) {
        // User has MFA enrolled, need to complete MFA challenge
        return { success: true, mfaRequired: true };
      }

      return { success: true };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Error de autenticación';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Sign up with tenant association
  const signUp = async (
    email: string,
    password: string,
    fullName: string,
    tenantId: string,
    role: UserRole
  ): Promise<{ success: boolean; error?: string }> => {
    setError(null);

    try {
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
            tenant_id: tenantId,
            role: role,
          },
        },
      });

      if (signUpError) {
        setError(signUpError.message);
        return { success: false, error: signUpError.message };
      }

      // Create user record in users table
      if (data.user) {
        const { error: insertError } = await supabase.from('users').insert({
          id: data.user.id,
          email: email,
          full_name: fullName,
          tenant_id: tenantId,
          role: role,
          status: 'active',
        });

        if (insertError) {
          if (import.meta.env.DEV) console.error('Failed to create user record:', insertError);
        }
      }

      return { success: true };
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Sign up failed';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Sign out
  const signOut = async () => {
    // Set loading to show UI feedback during logout
    setLoading(true);

    try {
      // Log LOGOUT event (non-blocking - don't wait for completion)
      if (tenant && user) {
        // Fire and forget with timeout - don't block logout on audit log
        Promise.race([
          auditLogService.logLogout(tenant.id, user.id),
          new Promise((_, reject) => setTimeout(() => reject(new Error('Audit log timeout')), 2000))
        ]).catch(err => { if (import.meta.env.DEV) console.warn('[Auth] Audit log failed (non-blocking):', err); });
      }

      // Sign out from Supabase with timeout
      await Promise.race([
        supabase.auth.signOut(),
        new Promise((_, reject) => setTimeout(() => reject(new Error('SignOut timeout')), 5000))
      ]).catch(err => {
        if (import.meta.env.DEV) console.warn('[Auth] Supabase signOut error (continuing with local cleanup):', err);
      });
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Auth] SignOut error:', err);
    } finally {
      // ALWAYS clear local state, even if Supabase signOut fails
      // This ensures the user can always "escape" a stuck session
      queryClient.clear();
      if (import.meta.env.DEV) console.log('[Auth] Query cache cleared on logout');

      // CRITICAL: Clear Supabase auth token from localStorage.
      // When supabase.auth.signOut() times out (unreachable server), the token
      // persists in localStorage. On next page load, getSession() reads the stale
      // token, tries to refresh it, hangs, and the app gets stuck in an auth loop.
      try {
        const keys = Object.keys(localStorage).filter(k => k.startsWith('sb-') && k.endsWith('-auth-token'));
        keys.forEach(k => localStorage.removeItem(k));
        if (keys.length > 0 && import.meta.env.DEV) console.log('[Auth] Cleared', keys.length, 'stale auth token(s) from localStorage');
      } catch {
        // localStorage may be unavailable in some contexts
      }

      setUser(null);
      setTenant(null);
      setSession(null);
      setError(null);
      setMfaRequired(false);
      setMfaFactors([]);
      setHandshakeComplete(false);
      setHandshakeResult(null);
      setOnboardingStatus('pending');
      setLoading(false);
    }
  };

  // Reset password
  const resetPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Password reset failed' };
    }
  };

  // Update password
  const updatePassword = async (newPassword: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err) {
      return { success: false, error: 'Password update failed' };
    }
  };

  // MFA: Enroll TOTP factor
  const enrollMFA = async (): Promise<{ success: boolean; qrCode?: string; secret?: string; factorId?: string; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'OmniTower Authenticator',
      });

      if (error) return { success: false, error: error.message };

      return {
        success: true,
        qrCode: data.totp.qr_code,
        secret: data.totp.secret,
        factorId: data.id,
      };
    } catch (err) {
      return { success: false, error: 'MFA enrollment failed' };
    }
  };

  // MFA: Verify enrollment
  const verifyMFA = async (factorId: string, code: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeError) return { success: false, error: challengeError.message };

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challengeData.id,
        code,
      });

      if (verifyError) return { success: false, error: verifyError.message };

      // Refresh MFA factors
      await checkMFAStatus();
      setMfaRequired(false);

      return { success: true };
    } catch (err) {
      return { success: false, error: 'MFA verification failed' };
    }
  };

  // MFA: Unenroll factor
  const unenrollMFA = async (factorId: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.mfa.unenroll({ factorId });
      if (error) return { success: false, error: error.message };

      await checkMFAStatus();
      return { success: true };
    } catch (err) {
      return { success: false, error: 'MFA unenrollment failed' };
    }
  };

  // MFA: Create challenge for sign-in
  const challengeMFA = async (factorId: string): Promise<{ success: boolean; challengeId?: string; error?: string }> => {
    try {
      const { data, error } = await supabase.auth.mfa.challenge({ factorId });
      if (error) return { success: false, error: error.message };
      return { success: true, challengeId: data.id };
    } catch (err) {
      return { success: false, error: 'MFA challenge creation failed' };
    }
  };

  // MFA: Verify challenge during sign-in
  const verifyMFAChallenge = async (factorId: string, challengeId: string, code: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { error } = await supabase.auth.mfa.verify({
        factorId,
        challengeId,
        code,
      });
      if (error) return { success: false, error: error.message };
      return { success: true };
    } catch (err) {
      return { success: false, error: 'MFA challenge verification failed' };
    }
  };

  // Impersonation: Start impersonating another user
  // - super_admin: Can impersonate any user across all tenants
  // - tenant_admin: Can only impersonate users within their own tenant
  const impersonateUser = async (userId: string): Promise<{ success: boolean; error?: string }> => {
    const canImpersonate = tenant?.role === 'super_admin' || tenant?.role === 'tenant_admin';
    if (!canImpersonate) {
      return { success: false, error: 'Only Admins can impersonate users' };
    }

    try {
      // Store original user
      setOriginalUser(user);

      // Fetch target user's profile
      const { data: targetUser, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (userError || !targetUser) {
        return { success: false, error: 'User not found' };
      }

      const { data: targetTenant, error: tenantError } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', targetUser.tenant_id)
        .single();

      if (tenantError || !targetTenant) {
        return { success: false, error: 'Tenant not found' };
      }

      // SECURITY: tenant_admin can only impersonate users within their OWN tenant
      if (tenant?.role === 'tenant_admin' && targetUser.tenant_id !== tenant.id) {
        return { success: false, error: 'Tenant admins can only impersonate users within their own tenant' };
      }

      // Log impersonation start for audit
      if (tenant) {
        auditLogService.logImpersonationStart(tenant.id, user?.id || '', userId);
      }

      // Set impersonation state (frontend only - backend still sees admin)
      setIsImpersonating(true);
      setTenant({
        id: targetUser.tenant_id,
        name: targetTenant.name || 'Desconocido',
        slug: targetTenant.slug || 'desconocido',
        role: targetUser.role as UserRole,
        maturity_level: targetTenant.maturity_level || 1,
        is_onboarded: targetUser.is_onboarded ?? targetTenant.is_onboarded ?? false,
        currency: targetTenant.currency_code || 'USD',
        locale: targetTenant.locale || 'es-CL',
      });

      return { success: true };
    } catch (err) {
      return { success: false, error: 'Impersonation failed' };
    }
  };

  // Impersonation: Stop impersonating
  const stopImpersonation = async () => {
    if (!isImpersonating || !originalUser) return;

    // Log impersonation end for audit
    if (tenant) {
      auditLogService.logImpersonationEnd(tenant.id, originalUser.id, user?.id || '');
    }

    setIsImpersonating(false);
    setUser(originalUser);
    setOriginalUser(null);

    // Restore original tenant
    await fetchTenantProfile(originalUser);
  };

  // Refresh session
  const refreshSession = async () => {
    const { data, error } = await supabase.auth.refreshSession();
    if (!error && data.session) {
      setSession(data.session);
      setUser(data.user);
      // Sync Realtime WebSocket with fresh token
      if (data.session.access_token) {
        syncRealtimeToken(data.session.access_token);
      }
    }
  };

  // Manual trigger for post-login handshake (for retry scenarios)
  const performHandshake = useCallback(async (): Promise<HandshakeResult> => {
    if (!tenant || !user) {
      return {
        success: false,
        serviceWorkerRegistered: false,
        indexedDBInitialized: false,
        criticalDataSynced: false,
        syncedItems: { stores: 0, products: 0 },
        errors: ['No authenticated user or tenant'],
      };
    }

    const result = await performPostLoginHandshake(tenant.id, user.id, {
      syncStores: true,
      syncProducts: false,
    });

    setHandshakeComplete(true);
    setHandshakeResult(result);
    return result;
  }, [tenant, user]);

  // Mark user as onboarded - transitions to 'operator' status
  const markOnboardingComplete = useCallback(async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('users')
        .update({ is_onboarded: true })
        .eq('id', user.id);

      if (error) {
        if (import.meta.env.DEV) console.error('[Handshake] Failed to mark user as onboarded:', error);
        return false;
      }

      // Update local tenant state and transition to operator status
      setTenant((prev) => (prev ? { ...prev, is_onboarded: true } : null));
      setOnboardingStatus('operator');
      if (import.meta.env.DEV) console.log('[Handshake] User marked as onboarded - transitioning to OPERATOR status');
      return true;
    } catch (err) {
      if (import.meta.env.DEV) console.error('[Handshake] Failed to mark onboarding complete:', err);
      return false;
    }
  }, [user]);

  // Error recovery: retry profile fetch without full page reload
  // Refreshes session first to ensure a valid token before retrying
  const retryProfile = useCallback(async () => {
    if (user) {
      setError(null);
      setOnboardingStatus('pending');
      setLoading(true);
      try {
        const { data, error } = await supabase.auth.refreshSession();
        if (error || !data.session) {
          setOnboardingStatus('error');
          setError('Sesión expirada. Por favor inicie sesión de nuevo.');
          setLoading(false);
          return;
        }
        setSession(data.session);
        setUser(data.session.user);
        await fetchTenantProfile(data.session.user);
      } catch {
        setOnboardingStatus('error');
        setLoading(false);
      }
    }
  }, [user, fetchTenantProfile]);

  const value: AuthContextType = {
    user,
    tenant,
    session,
    loading,
    error,
    mfaRequired,
    mfaFactors,
    handshakeComplete,
    handshakeResult,
    onboardingStatus,
    signIn,
    signUp,
    signOut,
    resetPassword,
    updatePassword,
    enrollMFA,
    verifyMFA,
    unenrollMFA,
    challengeMFA,
    verifyMFAChallenge,
    impersonateUser,
    stopImpersonation,
    isImpersonating,
    originalUser,
    refreshSession,
    performHandshake,
    markOnboardingComplete,
    retryProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
